import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { httpClient } from '@shared/api';

/** V2-4 этап 6: экран 8 «Диалоги» (бэкенд `cabinet_dialogs.py`). */

export type DlgPlatform = 'all' | 'widget' | 'telegram' | 'max';

export interface DlgFilter {
  platform: DlgPlatform;
  channelId?: string | null;
  days: number;
}

export interface DlgSummary {
  dialogs: number;
  clients: number;
  messages: number;
  unanswered: number;
}

export interface DlgPoint {
  t: string;
  dialogs: number;
  clients: number;
  unanswered: number;
}

export interface DlgSeries {
  step: 'day' | 'hour';
  points: DlgPoint[];
  overview: DlgPoint[];
  first_day: string | null;
}

export interface DlgItem {
  id: string;
  name: string;
  platform: 'widget' | 'telegram' | 'max';
  channel_title: string;
  last_at: string;
  last_text: string;
  last_direction: 'incoming' | 'outgoing';
  messages: number;
  unanswered: boolean;
}

export interface DlgPage {
  items: DlgItem[];
  total: number;
  next_cursor: string | null;
}

export interface DlgMessage {
  id: string;
  direction: 'incoming' | 'outgoing';
  content: string;
  created_at: string;
  no_answer: boolean;
  sources: Array<{ name: string; page: number | null; url: string | null }>;
}

export interface DlgDetail {
  id: string;
  name: string;
  platform: 'widget' | 'telegram' | 'max';
  channel_title: string;
  started_at: string;
  messages_count: number;
  messages: DlgMessage[];
  truncated: boolean;
}

const base = (aid: string) => `/assistants/${aid}/dialogs-v2`;
const fp = (f: DlgFilter) => ({
  platform: f.platform,
  channel_id: f.channelId || undefined,
  days: f.days,
});

export const dlgKeys = {
  all: ['dialogs-v2'] as const,
  summary: (aid: string, f: DlgFilter) => ['dialogs-v2', 'summary', aid, f] as const,
  series: (aid: string, f: object) => ['dialogs-v2', 'series', aid, f] as const,
  list: (aid: string, f: object) => ['dialogs-v2', 'list', aid, f] as const,
  detail: (aid: string, id: string) => ['dialogs-v2', 'detail', aid, id] as const,
};

export function useDlgSummary(aid: string, f: DlgFilter) {
  return useQuery<DlgSummary, Error>({
    queryKey: dlgKeys.summary(aid, f),
    queryFn: async () =>
      (await httpClient.get<DlgSummary>(`${base(aid)}/summary`, { params: fp(f) })).data,
    enabled: !!aid,
    placeholderData: keepPreviousData,
  });
}

export function useDlgSeries(
  aid: string,
  f: Omit<DlgFilter, 'days'> & { step: 'day' | 'hour'; from: string; to: string },
  enabled: boolean,
) {
  return useQuery<DlgSeries, Error>({
    queryKey: dlgKeys.series(aid, f),
    queryFn: async () =>
      (
        await httpClient.get<DlgSeries>(`${base(aid)}/series`, {
          params: {
            platform: f.platform,
            channel_id: f.channelId || undefined,
            step: f.step,
            date_from: f.from,
            date_to: f.to,
          },
        })
      ).data,
    enabled: !!aid && enabled,
    placeholderData: keepPreviousData,
  });
}

export function useDlgList(aid: string, f: DlgFilter & { q: string; unanswered: boolean }) {
  return useInfiniteQuery<DlgPage, Error>({
    queryKey: dlgKeys.list(aid, f),
    queryFn: async ({ pageParam }) =>
      (
        await httpClient.get<DlgPage>(`${base(aid)}/list`, {
          params: {
            ...fp(f),
            q: f.q || undefined,
            unanswered: f.unanswered || undefined,
            cursor: pageParam || undefined,
            limit: 50,
          },
        })
      ).data,
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: !!aid,
    placeholderData: keepPreviousData,
  });
}

export function useDlgDetail(aid: string, id: string | null) {
  return useQuery<DlgDetail, Error>({
    queryKey: dlgKeys.detail(aid, id ?? ''),
    queryFn: async () => (await httpClient.get<DlgDetail>(`${base(aid)}/${id}`)).data,
    enabled: !!aid && !!id,
  });
}
